import { z } from 'zod';
import { ROLES, USER_STATUSES, type Role, type UserStatus } from '../enums.js';
import {
  emailSchema,
  optionalText,
  paginationQuerySchema,
  phoneSchema,
  uuidSchema,
} from './common.js';
import { personNameSchema } from './auth.js';

export const createUserSchema = z.object({
  firstName: personNameSchema,
  lastName: personNameSchema,
  email: emailSchema,
  role: z.enum(ROLES),
  title: optionalText(120),
  phone: phoneSchema.optional(),
  /** Whoever this user reports to — drives TEAM-scoped visibility. */
  managerId: uuidSchema.nullish(),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserSchema = z.object({
  firstName: personNameSchema.optional(),
  lastName: personNameSchema.optional(),
  role: z.enum(ROLES).optional(),
  status: z.enum(USER_STATUSES).optional(),
  title: optionalText(120),
  phone: phoneSchema.optional(),
  managerId: uuidSchema.nullish(),
});
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

export const updateProfileSchema = z.object({
  firstName: personNameSchema.optional(),
  lastName: personNameSchema.optional(),
  title: optionalText(120),
  phone: phoneSchema.optional(),
  timezone: optionalText(64),
});
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

export const USER_SORT_FIELDS = ['createdAt', 'firstName', 'lastName', 'email', 'role'] as const;

export const listUsersQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(USER_SORT_FIELDS).default('createdAt'),
  role: z.enum(ROLES).optional(),
  status: z.enum(USER_STATUSES).optional(),
  managerId: uuidSchema.optional(),
});
export type ListUsersQuery = z.infer<typeof listUsersQuerySchema>;

/** The user shape returned by the API — never includes credential material. */
export interface UserSummary {
  id: string;
  organizationId: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  role: Role;
  status: UserStatus;
  title: string | null;
  phone: string | null;
  timezone: string | null;
  managerId: string | null;
  avatarUrl: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OrganizationSummary {
  id: string;
  name: string;
  slug: string;
  currency: string;
  timezone: string;
  createdAt: string;
}

/** Payload of `GET /auth/me` — everything the shell needs to render. */
export interface SessionUser extends UserSummary {
  organization: OrganizationSummary;
  permissions: string[];
}

export interface AuthTokensResponse {
  user: SessionUser;
  /** Lifetime of the access cookie in seconds; the client uses it to pre-refresh. */
  expiresIn: number;
  /** Double-submit CSRF token, echoed back in the `x-csrf-token` header. */
  csrfToken: string;
}
